import { useContext, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { InitDataContext } from "../../context/InitDataContext";
import * as api from "../../utils/Api.ts";
import type { ReportesFiltros } from "../../utils/Api.ts";
import ChartCard from "../widgets/graphs/ChartCard.tsx";
import MultiBarChart from "../widgets/graphs/MultiBarChart.tsx";
import DoughnutChart from "../widgets/graphs/DoughnutChart.tsx";
import {
  CHART_COLORS,
  CHART_PALETTE,
  formatMoney,
  formatNumber,
  formatPercent,
} from "../widgets/graphs/chartTheme.ts";
import { Count, Money } from "./ReportesCells.tsx";
import {
  Reporte,
  clean,
  estadoAprobacionColorPorNombre,
  formatDias,
  formatFecha,
  useReporte,
} from "./reportesUtils.ts";

/* Shape returned by /reports/{solicitudes|ordenes}/subrogantes */

export interface SubroganciaEstado {
  idEstado: number;
  estado: string;
  cantidad: number;
}

export interface SubroganciaPar {
  idTitular: number;
  titular: string;
  idSubrogante: number;
  subrogante: string;
  subroganciaActiva: boolean;
  acciones: number;
  // documents acted on: "solicitudes" or "ordenes" depending on the endpoint
  solicitudes?: number;
  ordenes?: number;
  aprobaciones: number;
  confirmaciones: number;
  rechazos: number;
  anulaciones: number;
  montoAprobadoClp: number;
  tasaRechazo: number;
  promedioDiasDesdeCreacion: number | null;
  primeraAccion: string | null;
  ultimaAccion: string | null;
  estados: SubroganciaEstado[];
}

export interface Subrogancia {
  totales: {
    pares: number;
    paresActivos: number;
    acciones: number;
    solicitudes?: number;
    ordenes?: number;
    aprobaciones: number;
    confirmaciones: number;
    rechazos: number;
    anulaciones: number;
    montoAprobadoClp: number;
    tasaRechazo: number;
    estados: SubroganciaEstado[];
  };
  detalle: SubroganciaPar[];
}

// GET /reports/subrogantes/resumen?idUser= -> the user's side only
export interface SubroganciaResumen {
  idUser: number;
  misSubrogantes: { solicitudes: Subrogancia; ordenes: Subrogancia };
  miActividadComoSubrogante: { solicitudes: Subrogancia; ordenes: Subrogancia };
}

type Tipo = "solicitudes" | "ordenes";

export const documentosPar = (p: SubroganciaPar) =>
  p.ordenes ?? p.solicitudes ?? 0;

// The user's own pairs (titular or subrogante) for one document type
export const paresPropios = (
  resumen: SubroganciaResumen | null,
  tipo: Tipo,
): SubroganciaPar[] => {
  const pares = [
    ...(resumen?.misSubrogantes?.[tipo]?.detalle ?? []),
    ...(resumen?.miActividadComoSubrogante?.[tipo]?.detalle ?? []),
  ];
  const vistos = new Set<string>();
  return pares.filter((p) => {
    const k = `${p.idTitular}-${p.idSubrogante}`;
    if (vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
};

// Excel sheet with the same visibility as the screen: every pair for the
// full-view users, only their own pairs for members, nothing for others
export const subroganciaSheets = (
  tipo: Tipo,
  verTodo: boolean,
  global: Subrogancia | null,
  propia: SubroganciaResumen | null,
) => {
  const pares = verTodo ? (global?.detalle ?? []) : paresPropios(propia, tipo);
  return pares.length
    ? [{ name: "Subrogancia", rows: subroganciaSheetRows(pares, tipo) }]
    : [];
};

// Excel rows (used by both dashboard exports)
export const subroganciaSheetRows = (pares: SubroganciaPar[], tipo: Tipo) =>
  pares.map((p) => ({
    Titular: clean(p.titular),
    Subrogante: clean(p.subrogante),
    "Subrogancia activa": p.subroganciaActiva ? "Sí" : "No",
    [tipo === "ordenes" ? "N° OC" : "N° solicitudes"]: documentosPar(p),
    Acciones: p.acciones,
    Aprobaciones: p.aprobaciones,
    Confirmaciones: p.confirmaciones,
    Rechazos: p.rechazos,
    Anulaciones: p.anulaciones,
    ...Object.fromEntries(
      (p.estados ?? []).map((e) => [`Estado: ${e.estado}`, e.cantidad]),
    ),
    "% rechazo": p.tasaRechazo,
    "Monto aprobado CLP": p.montoAprobadoClp,
    "Días promedio desde creación": p.promedioDiasDesdeCreacion,
    "Primera acción": formatFecha(p.primeraAccion),
    "Última acción": formatFecha(p.ultimaAccion),
  }));

// Count per state as tags colored like the rest of the app
const EstadosTags = ({ estados }: { estados: SubroganciaEstado[] }) =>
  estados?.length ? (
    <div className="fd-tags">
      {estados.map((e) => (
        <Tag
          key={e.idEstado}
          value={`${e.estado} ${formatNumber(e.cantidad)}`}
          style={{
            backgroundColor: estadoAprobacionColorPorNombre(e.estado),
            color: "white",
          }}
        />
      ))}
    </div>
  ) : (
    <span className="fd-sub">Sin acciones</span>
  );

const ActivaTag = ({ activa }: { activa: boolean }) => (
  <Tag
    value={activa ? "Activa" : "Inactiva"}
    severity={activa ? "success" : undefined}
    style={activa ? undefined : { backgroundColor: "#94a3b8" }}
  />
);

interface SubroganciaSectionProps {
  tipo: Tipo;
  // Every pair (only requested for the full-view users)
  global: Reporte<Subrogancia | null>;
  // The user's own pairs (/subrogantes/resumen?idUser=)
  propia: Reporte<SubroganciaResumen | null>;
  // Full view: users in USUARIOS_SUBROGANCIA_COMPLETA
  verTodo: boolean;
  // Dashboard filters (for the approvers request of the pies)
  query: ReportesFiltros;
  queryKey: string;
}

/**
 * Subrogancia statistics with three visibility levels:
 *  - full-view users (81, 65): every titular -> subrogante pair + their own
 *  - users in usuarios_subrogantes: only the pairs they belong to
 *  - everyone else: nothing (the section doesn't render)
 */
export default function SubroganciaSection({
  tipo,
  global,
  propia,
  verTodo,
  query,
  queryKey,
}: SubroganciaSectionProps) {
  const { initData } = useContext(InitDataContext);
  const yo = Number(initData?.id);
  const docLabel = tipo === "ordenes" ? "OC" : "Solicitudes";

  // Every pair: only ever filled for the full-view users
  const pares = useMemo(
    () => (verTodo ? (global.data?.detalle ?? []) : []),
    [verTodo, global.data],
  );

  const personal = propia;
  const misSubrogantes = useMemo(
    () => personal.data?.misSubrogantes?.[tipo]?.detalle ?? [],
    [personal.data, tipo],
  );
  const comoSubrogante = useMemo(
    () => personal.data?.miActividadComoSubrogante?.[tipo]?.detalle ?? [],
    [personal.data, tipo],
  );
  const esMiembro = misSubrogantes.length > 0 || comoSubrogante.length > 0;

  // Every pair with activity, actions stacked by state
  const chart = useMemo(() => {
    const conAcciones = pares
      .filter((p) => p.acciones > 0)
      .sort((a, b) => b.acciones - a.acciones);
    const estados = Array.from(
      new Map(
        conAcciones
          .flatMap((p) => p.estados ?? [])
          .map((e) => [e.idEstado, e.estado] as const),
      ),
    ).sort((a, b) => a[0] - b[0]);
    return {
      labels: conAcciones.map(
        (p) => `${clean(p.subrogante)} → ${clean(p.titular)}`,
      ),
      series: estados.map(([idEstado, estado]) => ({
        label: estado,
        data: conAcciones.map(
          (p) => p.estados?.find((e) => e.idEstado === idEstado)?.cantidad ?? 0,
        ),
        color: estadoAprobacionColorPorNombre(estado),
      })),
      count: conAcciones.length,
    };
  }, [pares]);

  const tabla = (
    rows: SubroganciaPar[],
    persona: "titular" | "subrogante" | "ambos",
    emptyMessage: string,
  ) => (
    <DataTable
      value={rows}
      size="small"
      sortMode="single"
      removableSort
      stripedRows
      emptyMessage={emptyMessage}
      className="fd-table"
    >
      {persona !== "titular" ? (
        <Column
          header="Subrogante"
          field="subrogante"
          sortable
          style={{ minWidth: "10rem" }}
          body={(p: SubroganciaPar) => (
            <span className="fd-prov-name">{clean(p.subrogante)}</span>
          )}
        />
      ) : null}
      {persona !== "subrogante" ? (
        <Column
          header={persona === "ambos" ? "Titular" : "Subrogando a"}
          field="titular"
          sortable
          style={{ minWidth: "10rem" }}
          body={(p: SubroganciaPar) => (
            <span className="fd-prov-name">{clean(p.titular)}</span>
          )}
        />
      ) : null}
      <Column
        header="Estado"
        field="subroganciaActiva"
        sortable
        body={(p: SubroganciaPar) => <ActivaTag activa={p.subroganciaActiva} />}
      />
      <Column
        header={docLabel}
        sortable
        sortField={tipo}
        body={(p: SubroganciaPar) => <Count value={documentosPar(p)} />}
      />
      <Column
        header="Acciones"
        field="acciones"
        sortable
        body={(p: SubroganciaPar) => <Count value={p.acciones} />}
      />
      <Column
        header="Por estado"
        style={{ minWidth: "12rem" }}
        body={(p: SubroganciaPar) => <EstadosTags estados={p.estados} />}
      />
      <Column
        header="% Rechazo"
        field="tasaRechazo"
        sortable
        body={(p: SubroganciaPar) => (
          <span className="fd-num">{formatPercent(p.tasaRechazo)}</span>
        )}
      />
      <Column
        header="Monto aprobado"
        field="montoAprobadoClp"
        sortable
        body={(p: SubroganciaPar) => <Money value={p.montoAprobadoClp} />}
      />
      <Column
        header="Días prom."
        field="promedioDiasDesdeCreacion"
        sortable
        headerTooltip="Promedio de días desde la creación del documento hasta la acción"
        body={(p: SubroganciaPar) => (
          <span className="fd-num">
            {formatDias(p.promedioDiasDesdeCreacion)}
          </span>
        )}
      />
      <Column
        header="Última acción"
        field="ultimaAccion"
        sortable
        body={(p: SubroganciaPar) => formatFecha(p.ultimaAccion)}
      />
    </DataTable>
  );

  const resumenTexto = (rows: SubroganciaPar[]) => {
    const acciones = rows.reduce((a, p) => a + p.acciones, 0);
    const docs = rows.reduce((a, p) => a + documentosPar(p), 0);
    const rechazos = rows.reduce((a, p) => a + p.rechazos, 0);
    return rows.length
      ? `${formatNumber(rows.length)} ${rows.length === 1 ? "subrogancia" : "subrogancias"} · ${formatNumber(
          docs,
        )} ${docLabel.toLowerCase()} · ${formatNumber(acciones)} acciones · ${formatNumber(
          rechazos,
        )} rechazos`
      : undefined;
  };

  const t = verTodo ? global.data?.totales : undefined;

  // Not a full-view user: only members of usuarios_subrogantes see anything,
  // and nothing renders until membership is known
  if (!verTodo && (personal.loading || personal.error || !esMiembro)) {
    return null;
  }

  return (
    <>
      <div className="rp-section">
        <h5 className="rp-section-title">
          <i className="pi pi-users" />
          Subrogancia
        </h5>
        <span className="rp-section-desc">
          Aprobaciones registradas por subrogantes (
          {tipo === "ordenes"
            ? "historial de aprobación de OC"
            : "historial de aprobación de solicitudes"}
          )
        </span>
      </div>

      {verTodo ? (
        <div className="fd-grid fd-grid-2-1">
          <ChartCard
            title="Todas las subrogancias"
            subtitle={
              t
                ? `${formatNumber(t.pares)} pares (${formatNumber(
                    t.paresActivos,
                  )} activos) · ${formatNumber(t.acciones)} acciones · ${formatPercent(
                    t.tasaRechazo,
                  )} rechazo · ${formatMoney(t.montoAprobadoClp)} aprobado`
                : undefined
            }
            loading={global.loading}
            error={global.error}
            onRetry={global.reload}
            empty={!pares.length}
            height={200}
          >
            {tabla(pares, "ambos", "Sin subrogancias registradas")}
          </ChartCard>

          <ChartCard
            title="Acciones por subrogante y estado"
            subtitle="Subrogante → titular"
            loading={global.loading}
            error={global.error}
            onRetry={global.reload}
            empty={!chart.count}
            emptyMessage="Sin acciones de subrogantes para estos filtros"
          >
            <MultiBarChart
              labels={chart.labels}
              series={chart.series}
              horizontal
              stacked
              money={false}
              maxLabelLength={30}
              height={Math.max(240, chart.count * 44)}
            />
          </ChartCard>
        </div>
      ) : null}

      {esMiembro ? (
        <>
          <div className="rp-section">
            <h5 className="rp-section-title">
              <i className="pi pi-user" />
              Tu subrogancia
            </h5>
            <span className="rp-section-desc">
              Quién aprueba en tu nombre y lo que apruebas como subrogante
            </span>
          </div>

          <SubroganciaTortas
            tipo={tipo}
            idUsuario={yo}
            misSubrogantes={misSubrogantes}
            comoSubrogante={comoSubrogante}
            query={query}
            queryKey={queryKey}
          />

          <div className="fd-grid fd-grid-1-1">
            <ChartCard
              title="Mis subrogantes"
              subtitle={
                resumenTexto(misSubrogantes) ?? "Quienes aprueban en tu nombre"
              }
              loading={personal.loading}
              error={personal.error}
              onRetry={personal.reload}
              height={160}
            >
              {tabla(
                misSubrogantes,
                "subrogante",
                "No tienes subrogantes registrados para estos filtros",
              )}
            </ChartCard>

            <ChartCard
              title="Mi actividad como subrogante"
              subtitle={
                resumenTexto(comoSubrogante) ??
                "Lo que apruebas en nombre de otros"
              }
              loading={personal.loading}
              error={personal.error}
              onRetry={personal.reload}
              height={160}
            >
              {tabla(
                comoSubrogante,
                "titular",
                "No registras subrogancias para estos filtros",
              )}
            </ChartCard>
          </div>
        </>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Share pies (mounted only for users in usuarios_subrogantes)          */
/* ------------------------------------------------------------------ */

interface AprobadorRow {
  idUsuario: number;
  acciones: number;
}

const COLOR_YO = CHART_COLORS.ordenado;

interface SubroganciaTortasProps {
  tipo: Tipo;
  idUsuario: number;
  misSubrogantes: SubroganciaPar[];
  comoSubrogante: SubroganciaPar[];
  query: ReportesFiltros;
  queryKey: string;
}

/**
 * Shares of approval actions. Actions are used instead of documents because
 * they add up exactly (one document can be acted on by both people).
 * The approvers report counts every action under whoever signed it, so
 * "own" actions = my approver total - my actions as subrogante.
 */
function SubroganciaTortas({
  tipo,
  idUsuario,
  misSubrogantes,
  comoSubrogante,
  query,
  queryKey,
}: SubroganciaTortasProps) {
  const aprobadores = useReporte<AprobadorRow[]>(
    () =>
      (tipo === "ordenes"
        ? api.GET_REPORTES.getOrdenesAprobadores
        : api.GET_REPORTES.getSolicitudesAprobadores)({
        ...query,
        limit: 1000,
      }),
    `${queryKey}|${tipo}`,
  );

  const datos = useMemo(() => {
    const total =
      aprobadores.data?.find((a) => Number(a.idUsuario) === idUsuario)
        ?.acciones ?? 0;
    const comoSubAcciones = comoSubrogante.reduce((a, p) => a + p.acciones, 0);
    const propias = Math.max(0, total - comoSubAcciones);
    const conAcciones = (rows: SubroganciaPar[]) =>
      rows.filter((p) => p.acciones > 0);

    const porMi = conAcciones(misSubrogantes);
    const paraOtros = conAcciones(comoSubrogante);
    const color = (i: number) => CHART_PALETTE[i % CHART_PALETTE.length];

    return {
      propias,
      quienAprobo: {
        labels: ["Yo", ...porMi.map((p) => clean(p.subrogante))],
        values: [propias, ...porMi.map((p) => p.acciones)],
        colors: [COLOR_YO, ...porMi.map((_, i) => color(i))],
        subrogantes: porMi.reduce((a, p) => a + p.acciones, 0),
      },
      miActividad: {
        labels: ["Propias", ...paraOtros.map((p) => `Por ${clean(p.titular)}`)],
        values: [propias, ...paraOtros.map((p) => p.acciones)],
        colors: [COLOR_YO, ...paraOtros.map((_, i) => color(i + 3))],
        comoSubrogante: comoSubAcciones,
      },
    };
  }, [aprobadores.data, idUsuario, misSubrogantes, comoSubrogante]);

  const pctDe = (parte: number, total: number) =>
    formatPercent(total ? (parte / total) * 100 : 0);
  const totalQuien = datos.propias + datos.quienAprobo.subrogantes;
  const totalActividad = datos.propias + datos.miActividad.comoSubrogante;
  const doc = tipo === "ordenes" ? "OC" : "solicitudes";

  return (
    <div className="fd-grid fd-grid-1-1">
      <ChartCard
        title="¿Quién aprobó por mí?"
        subtitle={
          datos.quienAprobo.subrogantes
            ? `${pctDe(datos.quienAprobo.subrogantes, totalQuien)} de las acciones sobre ${doc} las tomaron tus subrogantes`
            : "Acciones de aprobación: propias vs tus subrogantes"
        }
        loading={aprobadores.loading}
        error={aprobadores.error}
        onRetry={aprobadores.reload}
        empty={!datos.quienAprobo.subrogantes}
        emptyMessage="Tus subrogantes no registran acciones para estos filtros"
        height={260}
      >
        <DoughnutChart
          labels={datos.quienAprobo.labels}
          values={datos.quienAprobo.values}
          colors={datos.quienAprobo.colors}
          height={260}
          centerLabel={pctDe(datos.quienAprobo.subrogantes, totalQuien)}
          centerCaption="por subrogantes"
        />
      </ChartCard>

      <ChartCard
        title="Mi actividad: propia vs como subrogante"
        subtitle={
          datos.miActividad.comoSubrogante
            ? `${pctDe(datos.miActividad.comoSubrogante, totalActividad)} de tus acciones sobre ${doc} fueron como subrogante`
            : "Acciones de aprobación: propias vs en nombre de otros"
        }
        loading={aprobadores.loading}
        error={aprobadores.error}
        onRetry={aprobadores.reload}
        empty={!datos.miActividad.comoSubrogante}
        emptyMessage="No registras acciones como subrogante para estos filtros"
        height={260}
      >
        <DoughnutChart
          labels={datos.miActividad.labels}
          values={datos.miActividad.values}
          colors={datos.miActividad.colors}
          height={260}
          centerLabel={pctDe(datos.miActividad.comoSubrogante, totalActividad)}
          centerCaption="como subrogante"
        />
      </ChartCard>
    </div>
  );
}
